'use client';

/**
 * RoomAssignPicker — the disclosure control that assigns a cost-plan
 * line's unplaced quantity to a room-programme slot: a trigger button
 * that opens into a room select + qty input + Assign/Cancel pair.
 *
 * Extracted out of `costplan-lines-card.tsx`, where this exact control
 * was duplicated near-verbatim between `GroupHeaderRow` (the line's own
 * remainder) and `LeafRow` (an unassigned leaf's first placement) — one
 * component, two call sites.
 *
 * @component_role: Primitive
 * @component_type: Control
 * @domain: Canvas / Obeya
 * @layer: presentation
 */

import { useState } from 'react';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import type { RoomSlotOption } from './costplan-pivot-shared';

export interface RoomAssignPickerProps {
  roomSlots: RoomSlotOption[];
  mutating: boolean;
  onAssign: (slotSid: string, qty: number) => void;
  assignTestId?: string;
  /** Collapsed-state trigger text, e.g. "+ Assign to a room" or
   *  "120 unplaced · Assign to a room". */
  triggerLabel: string;
  triggerClassName: string;
  wrapperClassName: string;
}

export function RoomAssignPicker({
  roomSlots, mutating, onAssign, assignTestId, triggerLabel, triggerClassName, wrapperClassName,
}: RoomAssignPickerProps) {
  const [assigning, setAssigning] = useState(false);
  const [slotSid, setSlotSid] = useState('');
  const [assignQty, setAssignQty] = useState('');

  if (!assigning) {
    return (
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setAssigning(true); }}
        className={triggerClassName}
      >
        {triggerLabel}
      </button>
    );
  }

  return (
    <div className={wrapperClassName} onClick={(e) => e.stopPropagation()}>
      <select
        data-testid={assignTestId}
        value={slotSid}
        onChange={(e) => setSlotSid(e.target.value)}
        className="text-[11px] bg-background border border-border rounded px-1 py-0.5 max-w-[140px]"
      >
        <option value="">Room…</option>
        {roomSlots.map((s) => <option key={s.sid} value={s.sid}>{s.name}</option>)}
      </select>
      <input
        type="number" min="0" step="1" placeholder="qty"
        value={assignQty} onChange={(e) => setAssignQty(e.target.value)}
        className="w-14 text-right tabular-nums bg-background border border-border rounded px-1 py-0.5 text-[11px]"
      />
      <button
        type="button"
        disabled={mutating || !slotSid || !assignQty}
        onClick={() => {
          const v = parseFloat(assignQty);
          if (slotSid && !Number.isNaN(v) && v > 0) {
            onAssign(slotSid, v);
            setAssigning(false); setSlotSid(''); setAssignQty('');
          }
        }}
        className="text-[11px] font-medium text-primary disabled:opacity-40"
      >
        Assign
      </button>
      <button type="button" onClick={() => setAssigning(false)} className={cn(text.muted, 'hover:text-foreground')}>
        Cancel
      </button>
    </div>
  );
}
